/** Manual AdSense only — load adsbygoogle.js WITHOUT ?client= (avoids Auto ads). */
export const ADSENSE_CLIENT = 'ca-pub-2534229011090838';
export const ADSENSE_SLOT = '6432932865';

/** Only intrusive Auto / vignette injectors — never our [data-l123-manual-ad] banner. */
const AUTO_AD_SELECTOR =
  '.google-auto-placed, ins.adsbygoogle[data-vignette-loaded]';

const STYLE_ID = 'l123-block-auto-ads';

function getWinDoc(): { win: any; doc: any } {
  const g: any = typeof global !== 'undefined' ? global : {};
  const win = g.window || g;
  const doc = (win && win.document) || g.document;
  return { win, doc };
}

/** Serve ads on the live site only (avoid invalid traffic in local/dev). */
export function isAdSenseEnabled(): boolean {
  const g: any = typeof global !== 'undefined' ? global : {};
  const host = (g.window && g.window.location && g.window.location.hostname)
    || (g.location && g.location.hostname)
    || '';
  return host === 'language123.net' || String(host).endsWith('.language123.net');
}

/** True on standalone tools routes — no ads. */
export function isToolsPath(pathname?: string): boolean {
  const g: any = typeof global !== 'undefined' ? global : {};
  const path = pathname
    || (g.window && g.window.location && g.window.location.pathname)
    || (g.location && g.location.pathname)
    || '';
  return path === '/tools' || path.indexOf('/tools/') === 0 || path === '/record' || path.indexOf('/record/') === 0;
}

/**
 * Hub pages only (Test list / Part list). Practice detail paths must never show ads.
 * Matches /toeic2020, /toeic2020/test1 — not .../part5 or .../question1.
 */
export function isHubAdPath(pathname?: string): boolean {
  const g: any = typeof global !== 'undefined' ? global : {};
  const path = pathname
    || (g.window && g.window.location && g.window.location.pathname)
    || (g.location && g.location.pathname)
    || '';
  if (!path || path === '/') return true;
  if (isToolsPath(path)) return false;
  if (/^\/toeic\d+$/.test(path)) return true;
  if (/^\/toeic\d+\/test\d+$/.test(path)) return true;
  return false;
}

/** Remove vignette / Auto-placed injectors. Never touches [data-l123-manual-ad]. */
export function clearAutoPlacedAds(): void {
  try {
    const { doc } = getWinDoc();
    if (!doc || !doc.querySelectorAll) return;

    const nodes = doc.querySelectorAll(AUTO_AD_SELECTOR);
    for (let i = 0; i < nodes.length; i++) {
      const el = nodes[i];
      if (!el || !el.parentNode) continue;
      if (el.closest && el.closest('[data-l123-manual-ad]')) continue;
      el.parentNode.removeChild(el);
    }

    // Only remove fixed overlays that cover most of the viewport (true vignettes)
    const vh = (doc.documentElement && doc.documentElement.clientHeight) || 600;
    const vw = (doc.documentElement && doc.documentElement.clientWidth) || 800;
    const iframes = doc.querySelectorAll('iframe[id^="google_ads_iframe"], iframe[id^="aswift_"]');
    for (let i = 0; i < iframes.length; i++) {
      const frame = iframes[i];
      if (!frame || !frame.parentNode) continue;
      if (frame.closest && frame.closest('[data-l123-manual-ad]')) continue;
      const style = (doc.defaultView && doc.defaultView.getComputedStyle)
        ? doc.defaultView.getComputedStyle(frame)
        : null;
      if (!style || style.position !== 'fixed') continue;
      const w = frame.offsetWidth || 0;
      const h = frame.offsetHeight || 0;
      if (h > vh * 0.55 && w > vw * 0.55) {
        const parent = frame.parentElement;
        if (parent && parent.parentNode && parent !== doc.body) {
          parent.parentNode.removeChild(parent);
        } else {
          frame.parentNode.removeChild(frame);
        }
      }
    }
  } catch {
    /* ignore */
  }
}

export function injectAutoAdBlockCss(): void {
  try {
    const { doc } = getWinDoc();
    if (!doc || !doc.head) return;
    if (doc.getElementById(STYLE_ID)) return;
    const style = doc.createElement('style');
    style.id = STYLE_ID;
    style.type = 'text/css';
    // Hide Auto-placed + vignette only — do not hide all responsive/manual units
    style.appendChild(doc.createTextNode(`
      .google-auto-placed,
      ins.adsbygoogle[data-vignette-loaded] {
        display: none !important;
        height: 0 !important;
        max-height: 0 !important;
        overflow: hidden !important;
        margin: 0 !important;
        padding: 0 !important;
        opacity: 0 !important;
        pointer-events: none !important;
      }
      [data-l123-manual-ad] {
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
        pointer-events: auto !important;
        position: relative !important;
        left: auto !important;
        max-height: none !important;
      }
      [data-l123-manual-ad] ins.adsbygoogle {
        display: block !important;
        min-height: 90px !important;
      }
    `));
    doc.head.appendChild(style);
  } catch {
    /* ignore */
  }
}

/** Site-wide guard: CSS + cleanup of vignette / Auto-placed only. */
export function startAutoAdGuard(): () => void {
  injectAutoAdBlockCss();
  clearAutoPlacedAds();
  const { win, doc } = getWinDoc();
  let observer: any = null;
  let intervalId: any = null;
  let debounce: any = null;

  const run = () => {
    if (debounce) return;
    debounce = win.setTimeout(() => {
      debounce = null;
      clearAutoPlacedAds();
    }, 300);
  };

  try {
    if (doc && win.MutationObserver) {
      observer = new win.MutationObserver(run);
      observer.observe(doc.documentElement || doc.body, {
        childList: true,
        subtree: true,
      });
    }
  } catch {
    /* ignore */
  }

  if (win.setInterval) {
    intervalId = win.setInterval(clearAutoPlacedAds, 3000);
  }

  return () => {
    try {
      if (observer) observer.disconnect();
      if (intervalId && win.clearInterval) win.clearInterval(intervalId);
      if (debounce && win.clearTimeout) win.clearTimeout(debounce);
    } catch {
      /* ignore */
    }
  };
}
